import { datadogLogs } from '@datadog/browser-logs'
import { datadogRum } from '@datadog/browser-rum'
import { useMutation, UseMutationOptions, useQuery, UseQueryOptions } from '@tanstack/react-query'
import { GraphQLClient, Variables } from 'graphql-request'

import { useUserContext } from '@/context'

import { useRefresh } from '../useRefresh'

export const graphQLClient = new GraphQLClient(`${window.location.origin}/graphql`, {
  headers: {
    'Access-Control-Allow-Origin': '*',
  },
})

function extractFilesFromVariables(variables: Record<string, unknown>): {
  clone: Record<string, unknown>
  files: Map<File | Blob, string[]>
} {
  const files = new Map<File | Blob, string[]>()
  function walk(value: unknown, path: string): unknown {
    if (value instanceof File || value instanceof Blob) {
      const paths = files.get(value)
      if (paths) paths.push(path)
      else files.set(value, [path])
      return null
    }
    if (Array.isArray(value)) return value.map((item, i) => walk(item, `${path}.${i}`))
    if (value !== null && typeof value === 'object') {
      const result: Record<string, unknown> = {}
      for (const [key, val] of Object.entries(value as Record<string, unknown>)) {
        result[key] = walk(val, path ? `${path}.${key}` : key)
      }
      return result
    }
    return value
  }
  return { clone: walk(variables, '') as Record<string, unknown>, files }
}

async function graphqlRequest(queryString: string, payload: any, authToken?: string): Promise<any> {
  const { clone, files } = extractFilesFromVariables(payload)

  if (files.size === 0) {
    return graphQLClient.request(queryString, payload)
  }

  // Build multipart request per the GraphQL multipart request spec
  const form = new FormData()
  form.append('operations', JSON.stringify({ query: queryString, variables: clone }))

  const map: Record<string, string[]> = {}
  let i = 0
  files.forEach((paths) => {
    map[i] = paths.map((p) => `variables.${p}`)
    i++
  })
  form.append('map', JSON.stringify(map))

  i = 0
  files.forEach((_, file) => {
    form.append(String(i), file as File)
    i++
  })

  const headers: Record<string, string> = {}
  if (authToken) headers['Authorization'] = authToken

  const res = await fetch(`${window.location.origin}/graphql`, { method: 'POST', headers, body: form })
  const json = await res.json()

  if (!res.ok || json.errors?.length) {
    const err: any = new Error(json.errors?.[0]?.message ?? 'GraphQL error')
    err.response = { status: res.status, errors: json.errors }
    throw err
  }

  return json.data
}

export function useGraphql<TData = unknown, TError = unknown>(
  name: string,
  queryString: string,
  variables?: Variables,
  options?: Omit<UseQueryOptions<TData, TError>, 'queryKey' | 'queryFn'>,
) {
  const { user } = useUserContext()
  const refresh = useRefresh()
  if (user?.auth_token) {
    graphQLClient.setHeaders({ Authorization: user?.auth_token })
  }

  // Single-letter name means Vite minified a fn.name reference — cache key will collide
  if (name.length <= 2) {
    const msg = `[useGraphql] Suspicious query key "${name}" — likely a minified function name. Use an explicit QUERY_KEY string constant instead.`
    // eslint-disable-next-line no-console
    console.warn(msg) // nosemgrep
  }

  return useQuery<TData, TError>({
    queryKey: [name, variables],
    queryFn: async () => {
      try {
        const data = await graphQLClient.request<TData>(queryString, variables).catch((e) => {
          throw e
        })
        // eslint-disable-next-line no-console
        console.debug(`[useGraphql] ${name}`, { variables, data }) // nosemgrep
        return data
      } catch (error: any) {
        // eslint-disable-next-line no-console
        console.error(`[useGraphql] ${name} failed`, { variables, error }) // nosemgrep
        datadogRum.addError(error, { queryKey: name, variables })
        datadogLogs.logger.error(`[useGraphql] ${name} failed`, { queryKey: name, variables, error })
        if (error.response && error.response.status === 401) {
          await refresh.mutateAsync({})
        }
        throw error
      }
    },
    ...options,
    // Only fetch once authenticated — prevents 401s from firing before auth is ready,
    // which would permanently block the query (4xx errors are not retried).
    enabled: !!user?.auth_token && options?.enabled !== false,
  })
}

export function useGraphqlMutation<TVariables = void, TData = unknown, TError = unknown>(
  queryString: string,
  options?: UseMutationOptions<TData, TError, TVariables>,
) {
  const { user } = useUserContext()
  const refresh = useRefresh()
  if (user?.auth_token) {
    graphQLClient.setHeaders({ Authorization: user?.auth_token })
  }

  return useMutation({
    mutationFn: async (payload: any) => {
      try {
        const data = await graphqlRequest(queryString, payload, user?.auth_token).catch((e) => {
          throw e
        })
        // eslint-disable-next-line no-console
        console.debug(`[useGraphqlMutation]`, { payload, data }) // nosemgrep
        return data
      } catch (error: any) {
        // eslint-disable-next-line no-console
        console.error(`[useGraphqlMutation] failed`, { payload, error }) // nosemgrep
        datadogRum.addError(error, { payload })
        datadogLogs.logger.error(`[useGraphqlMutation] failed`, { payload, error })
        if (error.response && error.response.status === 401) {
          await refresh.mutateAsync({})
        }
        throw error
      }
    },
    ...options,
    retry: false,
  })
}
